import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from '@cad-arc';
import { MAP_WINDOW_PATH } from '../../router/constants';
import { EsriMapWindow } from './esri-map-window';
import { useEsriMapStore } from '../hooks/use-esri-map-store';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

const FREsriMapWrapper = styled('div')<{ $currentRouteIsMap: boolean }>`
    visibility: ${(props) => (props.$currentRouteIsMap ? 'visible' : 'hidden')};
    width: ${(props) => (props.$currentRouteIsMap ? '100%' : '0')};
    height: ${(props) => (props.$currentRouteIsMap ? '100%' : '0')};
`;

/**
 * define the max allowed inactive time (in milliseconds) allowed before map gets reloaded.
 * 2 major root causes of blank map
 *  - basemap failed to load due to unstable network connectivity
 *  - webgl lost context
 *
 * if MAP_MAX_ALLOWED_INACTIVE_TIME is small, e.g 10 min, the chance of map reload increases, however if FR moves to weak network connectivity areas, the failures of basemap load will increase.
 * if MAP_MAX_ALLOWED_INACTIVE_TIME is big, the chance of webgl crashes will increase (possibly. we haven't reproduce webgl issue on our end).
 *
 * currently we have identified basemap load failure is a common issue for many tenants. Before we enabled offline basemap, we should reduce the chance of map reload.
 * webgl issue is more related to hardware, if the MDT is not upgraded, they may always hit the webgl issue.
 *
 * So set MAP_MAX_ALLOWED_INACTIVE_TIME to maximum value for now (equivalent to disbale map reload based on inactive time),
 * once we have more evidence for the correlation between 'webgl lost context' error and 'MAP_MAX_ALLOWED_INACTIVE_TIME',
 * we can adjust the inactive time.
 */
const MAP_MAX_ALLOWED_INACTIVE_TIME = Number.MAX_SAFE_INTEGER; // ms

export function FREsriMapWindowEnabler() {
    const location = useLocation();
    const [mapLastInactiveTimestamp, setMapLastInactiveTimestamp] = useState(0);
    const mapStore = useEsriMapStore();
    const { firstResponderMapSelfFocus } = useEnabledFeatures();

    const currentRouteIsMap = location.pathname === MAP_WINDOW_PATH;
    const currentTimestamp = new Date().getTime();
    // when mapLastInactiveTimestamp === 0 (initial state, first time load map)
    // OR map inactive time does not exceed the max allowed inactive time,
    // we keep the map div in dom.
    // if the map inactive time exceed the max allowed inactive time, map will be destroyed.
    // then useEffect resetMapInactiveTimestamp will reset mapLastInactiveTimestamp to 0, which will
    // trigger map div re-mount to dom
    useEffect(
        function resetMapInactiveTimestamp() {
            if (
                currentRouteIsMap &&
                mapLastInactiveTimestamp &&
                currentTimestamp - mapLastInactiveTimestamp > MAP_MAX_ALLOWED_INACTIVE_TIME
            ) {
                setMapLastInactiveTimestamp(0);
            }
        },
        [mapLastInactiveTimestamp, currentRouteIsMap, currentTimestamp]
    );

    useEffect(
        function clearFollowingUnitIdInMapStore() {
            // if we are currently not at the map window (switched to the other app tab from it),
            // and user's unit was auto selected once, but then deselected
            // and no other entity was selected after (!mapStore.isMapLedgerOpen) need to clear
            // the initial following user's unit id to auto select this user's unit again when return to the map
            if (!currentRouteIsMap && mapStore.followingUnitId && !mapStore.isMapLedgerOpen) {
                mapStore.setFollowingUnitId(undefined);
            }

            if (!currentRouteIsMap) {
                mapStore.setShouldMoveMapCenterToLoggedInUnit(firstResponderMapSelfFocus);
            }
        },
        [currentRouteIsMap, mapStore, firstResponderMapSelfFocus]
    );

    const isMapActive =
        mapLastInactiveTimestamp === 0 ||
        currentTimestamp - mapLastInactiveTimestamp <= MAP_MAX_ALLOWED_INACTIVE_TIME;

    if (isMapActive) {
        return (
            <FREsriMapWrapper $currentRouteIsMap={currentRouteIsMap} style={{ margin: 'auto' }}>
                <EsriMapWindow
                    isMapInactive={!currentRouteIsMap}
                    mapLastInactiveTimestamp={mapLastInactiveTimestamp}
                    setMapLastInactiveTimestamp={setMapLastInactiveTimestamp}
                />
            </FREsriMapWrapper>
        );
    }

    return null;
}
